import {
  AdgroupFormData,
  CampaignFormData,
  InterestOption,
  InterestScene,
  InterestSearchResult,
} from '@tt4b/ttam-enums';
import { Ref, computed, ref, watch } from '@vue/composition-api';
import { useInterstSearchResultApi, useInterstOptionsApi, useRequest } from '@tt4b/ttam-hooks';

const inMarketList = ref<InterestOption[]>([]);
const inMarketListObj = {};
const inMarketSearchList = ref<InterestSearchResult[]>([]);
const keyword = ref<string>('');

function findTopParent(item) {
  if (item?.parent?.id) {
    return findTopParent(item.parent);
  }

  return item;
}

function handleCategories(option, obj = {}) {
  const { children } = option;
  if (!children?.length) {
    return;
  }
  children.forEach(item => {
    item.parent = option;
    obj[item.id] = item;
    handleCategories(item, obj);
  });
}

export function useInMarketCategoryPropsOptions(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData } = props;
  const inMarketOptions = computed(() => {
    if (keyword.value) {
      const searchListIds = inMarketSearchList.value.map(item => item.id);
      const notInSearchListIds = adgroupData.value?.inMarketTags?.filter?.(id => !searchListIds.includes(id));

      return [
        ...inMarketSearchList.value,
        ...notInSearchListIds.map(id => ({ ...inMarketListObj[id], children: [], visible: false, filterable: false })),
      ];
    }

    return inMarketList.value;
  });

  return {
    options: inMarketOptions,
  };
}

export function useInMarketInterestOptionsDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;

  const { isLoading, error, isEmpty, run } = useRequest(
    useInterstOptionsApi,
    {
      scene: InterestScene.INMARKET,
      inventory: adgroupData.value.inventoryFlow,
      industryTypes: campaignData.value.industryTypes,
    },
    {
      manual: true,
    },
  );

  watch(error, newVal => {
    if (newVal) {
      inMarketList.value = [];
    }
  });
  return {
    data: inMarketList,
    isLoading,
    error,
    isEmpty,
    run: () => {
      if (inMarketList.value.length) {
        return;
      }
      run().then(data => {
        handleCategories({ children: data }, inMarketListObj);
        inMarketList.value = data;
      });
    },
  };
}

export function useInMarketInterestSearchResultDataSource(props: { campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = props;
  const outerLoading = ref(false);

  const { isEmpty, isLoading, error, run, cancel } = useRequest(useInterstSearchResultApi, {} as any, {
    manual: true,
  });

  watch(error, newVal => {
    if (newVal) {
      inMarketSearchList.value = [];
      outerLoading.value = false;
    }
  });
  let timer;

  return {
    data: inMarketSearchList,
    error,
    isLoading: computed(() => isLoading.value || outerLoading.value),
    isEmpty,
    run: (word: string) => {
      cancel?.();
      keyword.value = word;
      outerLoading.value = true;
      if (timer) {
        window.clearTimeout(timer);
      }

      timer = window.setTimeout(() => {
        run({
          keyword: word,
          scenes: [InterestScene.INMARKET],
          industryTypes: campaignData.value.industryTypes,
        }).then(data => {
          if (word !== keyword.value) {
            return;
          }
          inMarketSearchList.value = data.map(item => {
            const parent = inMarketListObj[item.keyword_id];
            return {
              ...item,
              parent,
              id: item.keyword_id,
              i18n_value: item.keyword,
              type: findTopParent(parent)?.i18n_value,
            } as any;
          });

          outerLoading.value = false;
        });
      });
    },
  };
}
